import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Play, Sparkles, Zap, TrendingUp, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const HeroSection = () => {
  const [heroTitle, setHeroTitle] = useState("");
  const [isSimulating, setIsSimulating] = useState(false);
  const [showSignupGate, setShowSignupGate] = useState(false);
  const navigate = useNavigate();

  const handleSimulate = () => {
    if (!heroTitle.trim()) return;
    trackEvent("Hero", "simulate_click", heroTitle);
    setIsSimulating(true);
    setTimeout(() => {
      setIsSimulating(false);
      setShowSignupGate(true);
    }, 3000);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Effects */}
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-purple/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-pink/10 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/80 border border-border backdrop-blur-sm mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-brand-purple" />
            <span className="text-sm font-medium text-muted-foreground">
              Trusted by 50,000+ content creators
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Create High-CTR YouTube{" "}
            <span className="gradient-text">Thumbnails with AI</span>{" "}
            in Seconds
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 animate-fade-in text-balance" style={{ animationDelay: "0.2s" }}>
            Boost clicks, views, and engagement without any design skills. 
            Generate scroll-stopping thumbnails that convert viewers into subscribers.
          </p>

          {/* Interactive Simulator CTA */}
          <div className="max-w-xl mx-auto mb-16 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Entrez le titre de votre vidéo..."
                value={heroTitle}
                onChange={(e) => setHeroTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSimulate()}
                className="h-14 text-lg bg-card/80 backdrop-blur-sm border-border/50 flex-1"
                disabled={isSimulating}
              />
              <Button
                variant="hero"
                size="xl"
                className="group shrink-0"
                onClick={handleSimulate}
                disabled={isSimulating || !heroTitle.trim()}
              >
                {isSimulating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Générer
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Essayez gratuitement — aucune carte bancaire requise
            </p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: "0.4s" }}>
            {[
              { icon: Zap, value: "50K+", label: "Thumbnails Created" },
              { icon: TrendingUp, value: "2.5x", label: "Average CTR Boost" },
              { icon: Sparkles, value: "10K+", label: "Happy Creators" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <stat.icon className="w-5 h-5 text-brand-purple" />
                  <span className="text-2xl md:text-3xl font-bold gradient-text">{stat.value}</span>
                </div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Hero Image/Preview */}
        <div className="mt-16 md:mt-24 relative animate-fade-in-up" style={{ animationDelay: "0.5s" }}>
          <div className="relative max-w-5xl mx-auto">
            <div className="absolute inset-0 gradient-bg opacity-20 blur-3xl rounded-3xl" />
            
            <div className="relative bg-card/80 backdrop-blur-xl border border-border rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/50">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-destructive/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 rounded-lg bg-background/50 text-xs text-muted-foreground">
                    thumbgenius.ai/generate
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-10">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="hero-video-title" className="text-sm font-medium text-muted-foreground mb-2 block">
                        Video Title
                      </label>
                      <div id="hero-video-title" role="textbox" aria-readonly="true" className="px-4 py-3 rounded-xl bg-background border border-border text-foreground">
                        How I Made $100K with YouTube in 2024
                      </div>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      {["🔥 Exciting", "💰 Money", "📈 Growth", "🎯 Success"].map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Button variant="gradient" className="w-full">
                      <Sparkles className="w-4 h-4" />
                      Generate Thumbnails
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="aspect-video rounded-xl bg-gradient-to-br from-secondary to-muted flex items-center justify-center border border-border/50 hover:border-primary/50 transition-colors cursor-pointer group"
                      >
                        <div className="text-center">
                          <div className="w-10 h-10 rounded-full gradient-bg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                            <Sparkles className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <span className="text-xs text-muted-foreground">Thumbnail {i}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signup Gate Modal */}
      <Dialog open={showSignupGate} onOpenChange={setShowSignupGate}>
        <DialogContent className="sm:max-w-md border-primary/30 bg-card">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">
              Vos miniatures sont prêtes ! 🎨
            </DialogTitle>
            <DialogDescription className="text-center text-muted-foreground pt-2">
              Créez un compte gratuit pour voir vos 4 miniatures générées et les télécharger en HD.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="aspect-video rounded-xl bg-gradient-to-br from-secondary to-muted flex items-center justify-center border border-border/50 relative overflow-hidden"
                >
                  <div className="absolute inset-0 backdrop-blur-md bg-background/60" />
                  <Sparkles className="w-6 h-6 text-primary relative z-10" />
                </div>
              ))}
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
              <Zap className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">Rejoignez 10 000+ créateurs</span> qui boostent leurs vues avec ThumbGenius.
              </p>
            </div>

            <Link to="/signup" className="block">
              <Button variant="gradient" size="lg" className="w-full btn-shimmer font-bold text-base gap-2">
                <Sparkles className="w-5 h-5" />
                Créer mon compte gratuit
              </Button>
            </Link>

            <p className="text-[11px] text-center text-muted-foreground">
              Gratuit • 3 générations offertes • Sans carte bancaire
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HeroSection;
